/**
 * runAgentStream — POST /agent-run/{agentKey}/stream and parse the SSE progress.
 *
 * The platform api client doesn't expose the raw streaming body, so this makes a
 * direct fetch with the same auth headers (Bearer + X-CBO-ID + X-Tenant-ID) and
 * parses `data: {json}\n\n` events, invoking onEvent for each. Best-effort — any
 * transport error surfaces as an {type:'error'} event.
 */
import { config } from '@/src/config';

export type AgentStreamEvent =
  | { type: 'step'; tool: string; status?: string; ok?: boolean }
  | { type: 'artifact'; page_id: string }
  | { type: 'summary'; summary: string; pending_approvals?: { tool: string }[] }
  | { type: 'done' }
  | { type: 'error'; error: string };

export async function runAgentStream(
  agentKey: string,
  body: { instruction: string; cbo_id?: number; approved?: boolean },
  onEvent: (ev: AgentStreamEvent) => void,
): Promise<void> {
  const base = String((config as any)?.weaveinApi?.baseUrl || '').replace(/\/$/, '');
  const url = `${base}/api/platform/v1/agent-run/${encodeURIComponent(agentKey)}/stream`;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('auth_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const cbo = localStorage.getItem('active_cbo_id');
  if (cbo) headers['X-CBO-ID'] = cbo;
  const tenant = localStorage.getItem('platform_tenant_slug');
  if (tenant) headers['X-Tenant-ID'] = tenant;

  let res: Response;
  try {
    res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
  } catch (e: any) {
    onEvent({ type: 'error', error: e?.message || 'network error' });
    return;
  }
  if (!res.ok || !res.body) {
    onEvent({ type: 'error', error: `request failed (${res.status})` });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() || '';
    for (const frame of frames) {
      const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!dataLine) continue;
      const payload = dataLine.slice(5).trim();
      if (!payload) continue;
      try {
        onEvent(JSON.parse(payload) as AgentStreamEvent);
      } catch {
        /* skip malformed frame */
      }
    }
  }
}
